import { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { CitationOffender } from '@/components/citation/CitationOffender';
import { SearchModal } from '@/components/modals/SearchModal';
import { PageModal } from '@/components/modals/PageModal';
import { NPSDPersonScreen } from '@/components/npsd/NPSDPersonScreen';
import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import moment from 'moment';

interface CitationOffenderSectionProps {
  citation: any;
  isEditable: boolean;
  isIssuedFieldLocked: boolean;
  showValidationErrors: boolean;
  offenderError?: string;
  theme: string;
  onPersonIdChange: (personId: number | null) => void;
  onDirty: () => void;
}

export const CitationOffenderSection = memo(function CitationOffenderSection({
  citation,
  isEditable,
  isIssuedFieldLocked,
  showValidationErrors,
  offenderError,
  theme,
  onPersonIdChange,
  onDirty,
}: CitationOffenderSectionProps) {
  // Offender form state
  const [offenderData, setOffenderData] = useState<any>({
    firstName: '', middleName: '', lastName: '', suffix: '',
    dob: null, driversLicenseNum: '', driversLicenseState: '',
    phoneNumber: '', address: '', city: '', state: '', zip: '',
  });
  const [selectedOffender, setSelectedOffender] = useState<any>(null);

  // Search modal state
  const [showPersonSearchModal, setShowPersonSearchModal] = useState(false);

  // Linked person view modal state
  const [showLinkedPersonModal, setShowLinkedPersonModal] = useState(false);
  const [linkedPersonModalId, setLinkedPersonModalId] = useState<number | null>(null);
  const [linkedPersonAllowEdit, setLinkedPersonAllowEdit] = useState(false);

  // Load person data when citation has npsdPersonId but no snapshot and we don't have it yet
  useEffect(() => {
    if (citation.npsdPersonId && !citation.offenderSnapshot && !selectedOffender) {
      api.getNPSDItemById(String(citation.npsdPersonId), 'person', (result: any) => {
        const person = new (window as any).Person(result);
        setSelectedOffender(person);
        setOffenderData({
          firstName: person.firstName || '',
          middleName: person.middleName || '',
          lastName: person.lastName || '',
          suffix: person.suffix || '',
          dob: person.dob,
          driversLicenseNum: person.driversLicenseNum || '',
          driversLicenseState: person.driversLicenseState || '',
          phoneNumber: person.phoneNumbers?.[0]?.phoneNumber || '',
          address: person.locAddress || '',
          city: person.locCity || '',
          state: person.locState || '',
          zip: person.locZip || '',
        });
      });
    }
  }, [citation.npsdPersonId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Computed: offender display items (for issued/voided citations)
  const getOffenderSource = useCallback((): any => {
    if ((citation.status === 'issued' || citation.status === 'voided') && citation.offenderSnapshot) {
      return citation.offenderSnapshot;
    }
    return selectedOffender || offenderData;
  }, [citation.status, citation.offenderSnapshot, selectedOffender, offenderData]);

  const offenderDisplayItems = useMemo(() => {
    const src = getOffenderSource();
    if (!src) return [];
    const fullName = [src.firstName, src.middleName, src.lastName, src.suffix].filter(Boolean).join(' ') || '-';
    const dobVal = src.dob || src.dateOfBirth;
    const dob = dobVal ? moment(dobVal).format('MM/DD/YYYY') : '-';
    const idNum = src.driversLicenseNum || src.idNumber || '-';
    const idState = src.driversLicenseState || src.idState || '-';
    const idType = src.idType || src.idTypeValue || '-';
    const phone = src.phone || src.phoneNumber || (src.phoneNumbers?.[0]?.phoneNumber) || '-';
    let address = src.locFormattedAddress;
    if (!address && (src.address || src.city || src.state || src.zip)) {
      address = [src.address, src.city, src.state, src.zip].filter(Boolean).join(', ');
    }
    address = address || '-';
    return [
      { label: 'Name', value: fullName },
      { label: 'Date of Birth', value: dob },
      { label: 'Gender', value: src.genderValue || '-' },
      { label: 'Race', value: src.raceValue || '-' },
      { label: 'ID Type', value: idType },
      { label: 'ID Number', value: idNum },
      { label: 'ID State', value: idState },
      { label: 'Phone', value: phone },
      { label: 'Address', value: address },
    ];
  }, [getOffenderSource]);

  const personSearchInitialValues = useMemo(() => ({
    firstName: offenderData.firstName || '',
    middleName: offenderData.middleName || '',
    lastName: offenderData.lastName || '',
    dob: offenderData.dob || '',
    driversLicenseNum: offenderData.driversLicenseNum || '',
    address: offenderData.address || '',
  }), [offenderData]);

  // Handlers
  const setLinkedPerson = useCallback((person: any) => {
    setSelectedOffender(person);
    onPersonIdChange(person.id);
    setOffenderData({
      firstName: person.firstName || '',
      middleName: person.middleName || '',
      lastName: person.lastName || '',
      suffix: person.suffix || '',
      dob: person.dob,
      driversLicenseNum: person.driversLicenseNum || '',
      driversLicenseState: person.driversLicenseState || '',
      phoneNumber: person.phoneNumbers?.[0]?.phoneNumber || '',
      address: person.locAddress || '',
      city: person.locCity || '',
      state: person.locState || '',
      zip: person.locZip || '',
    });
    onDirty();
  }, [onPersonIdChange, onDirty]);

  const handleOffenderChange = useCallback((data: any) => {
    setOffenderData(data);
    onDirty();
  }, [onDirty]);

  const handleLinkPerson = useCallback(() => {
    setShowPersonSearchModal(true);
  }, []);

  const handleCreatePerson = useCallback((personData: any) => {
    if (!personData.firstName || !personData.lastName) {
      App.showPopupNotification('First name and last name are required.', 'error');
      return;
    }
    const newPerson: any = {
      firstName: personData.firstName || null,
      middleName: personData.middleName || null,
      lastName: personData.lastName || null,
      suffix: personData.suffix || null,
      dob: personData.dob || null,
      driversLicenseNum: personData.driversLicenseNum || null,
      driversLicenseState: personData.driversLicenseState || null,
      locAddress: personData.address || null,
      locCity: personData.city || null,
      locState: personData.state || null,
      locZip: personData.zip || null,
      phoneNumbers: personData.phoneNumber ? [{ phoneType: 1, phoneNumber: personData.phoneNumber }] : [],
    };
    api.saveNPSDItem(newPerson, 'person', (result: any) => {
      App.showPopupNotification('Person created and linked successfully.', 'success');
      const person = new (window as any).Person(result);
      setLinkedPerson(person);
    });
  }, [setLinkedPerson]);

  const handleUnlinkPerson = useCallback(() => {
    onPersonIdChange(null);
    setSelectedOffender(null);
    onDirty();
  }, [onPersonIdChange, onDirty]);

  const handleClearOffender = useCallback(() => {
    setSelectedOffender(null);
    onPersonIdChange(null);
    setOffenderData({
      firstName: '', middleName: '', lastName: '', suffix: '',
      dob: null, driversLicenseNum: '', driversLicenseState: '',
      phoneNumber: '', address: '', city: '', state: '', zip: '',
    });
    onDirty();
  }, [onPersonIdChange, onDirty]);

  const handleShowLinkedPerson = useCallback(() => {
    if (citation.npsdPersonId) {
      setLinkedPersonModalId(citation.npsdPersonId);
      setLinkedPersonAllowEdit(citation.status === 'draft');
      setShowLinkedPersonModal(true);
    }
  }, [citation.npsdPersonId, citation.status]);

  const handlePersonSearchSelect = useCallback((personData: any) => {
    setShowPersonSearchModal(false);
    const person = new (window as any).Person(personData);
    setLinkedPerson(person);
  }, [setLinkedPerson]);

  const handlePersonSearchView = useCallback((personData: any) => {
    if (personData?.id) {
      setLinkedPersonModalId(personData.id);
      setLinkedPersonAllowEdit(false);
      setShowLinkedPersonModal(true);
    }
  }, []);

  const handlePersonUpdatedInModal = useCallback(() => {
    // Refetch by ID to guarantee full data — the API update result may be sparse.
    if (!citation.npsdPersonId || citation.npsdPersonId !== linkedPersonModalId) return;
    api.getNPSDItemById(String(citation.npsdPersonId), 'person', (full: any) => {
      const person = new (window as any).Person(full);
      setLinkedPerson(person);
    });
  }, [citation.npsdPersonId, linkedPersonModalId, setLinkedPerson]);

  const handleCloseLinkedPersonModal = useCallback(() => {
    setShowLinkedPersonModal(false);
    setLinkedPersonModalId(null);
    setLinkedPersonAllowEdit(false);
  }, []);

  return (
    <>
      <div className="section">
        <div className="section-header"><span className="section-title">Offender</span></div>
        <div className="section-body">
          {offenderError && (
            <div className="alert alert-danger">{offenderError}</div>
          )}
          {(citation.status === 'issued' || citation.status === 'voided') && citation.offenderSnapshot ? (
            <div className="display-panel">
              <div className="display-grid">
                {offenderDisplayItems.map((item, i) => (
                  <div key={i} className="display-item">
                    <span className="display-label">{item.label}</span>
                    <span className={`display-value ${!item.value || item.value === '-' ? 'empty' : ''}`}>{item.value}</span>
                  </div>
                ))}
              </div>
              <div className="snapshot-notice">
                Showing information captured at time of issuance.
                {citation.npsdPersonId && (
                  <>{' '}<a href="#" onClick={(e) => { e.preventDefault(); handleShowLinkedPerson(); }}>View current person record</a></>
                )}
              </div>
            </div>
          ) : (
            <CitationOffender
              value={offenderData}
              npsdPersonId={citation.npsdPersonId}
              isReadOnly={!isEditable}
              isLocked={isIssuedFieldLocked}
              onInput={handleOffenderChange}
              onChange={handleOffenderChange}
              onLinkPerson={handleLinkPerson}
              onCreatePerson={handleCreatePerson}
              onUnlinkPerson={handleUnlinkPerson}
              onShowLinkedPerson={handleShowLinkedPerson}
              onClear={handleClearOffender}
            />
          )}
        </div>
      </div>

      {/* Search Modal */}
      <SearchModal
        open={showPersonSearchModal}
        searchType={'person' as 'person'}
        title="Link to Person"
        theme={theme}
        initialValues={personSearchInitialValues}
        onSelect={handlePersonSearchSelect}
        onView={handlePersonSearchView}
        onCancel={() => setShowPersonSearchModal(false)}
      />

      {/* Linked Person View Modal */}
      <PageModal
        open={showLinkedPersonModal}
        modalId="citationLinkedPerson"
        title="Linked Person"
        theme={theme}
        onClose={handleCloseLinkedPersonModal}
      >
        {showLinkedPersonModal && linkedPersonModalId && (
          <NPSDPersonScreen
            personId={linkedPersonModalId}
            isWindow={true}
            onClose={handleCloseLinkedPersonModal}
            onPersonUpdated={handlePersonUpdatedInModal}
            onPersonSaved={handlePersonUpdatedInModal}
          />
        )}
      </PageModal>
    </>
  );
});
